/* @flow */
import { createSelector } from '@reduxjs/toolkit';
import {
  getLiveChatOptions,
  getLiveChatProvider,
} from 'plugin/live-chat/util/liveChatUtils';
import { getLiveChatEnvOptions } from 'src/shared/app/base/selector/appEnvSelectors';
import { getMembershipEmailAddress } from '@braindate/domain/lib/membership/util';
import {
  getUserFullName,
  getUserId,
  getUserAvatar,
} from '@braindate/domain/lib/user/util';
import { getEventColor } from '@braindate/domain/lib/event/util';
import type { State } from 'src/shared/core/type/reduxTypes';
import { getEventSafely } from 'src/shared/domain/event/selector/eventSelectors';
import { getMembership } from 'src/shared/domain/membership/selector/membershipSelectors';
import { getSelf } from 'src/shared/domain/user/selector/userSelectors';
import {
  updateOpenedStatus,
  updateStatus,
} from 'plugin/live-chat/action/liveChatAction';
import { intercomWidgetActions } from 'plugin/live-chat/providers/liveChatIntercom';
import { tawkWidgetActions } from 'plugin/live-chat/providers/liveChatTawk';
import {
  LIVE_CHAT_PROVIDER_INTERCOM,
  LIVE_CHAT_PROVIDER_TAWK,
} from 'plugin/live-chat/constant/liveChatConstant';
import type {
  WidgetLoadParams,
  InternalLiveChatActions,
} from 'plugin/live-chat/type/liveChatType';
import { getLocale } from 'src/shared/app/locale/selector/localeSelectors';

/*
 |------------------------------------------------------------------------------
 | Base selectors
 |------------------------------------------------------------------------------
 */

const getState = (state) => state.app.plugins.liveChat;

/*
 |------------------------------------------------------------------------------
 | Exports
 |------------------------------------------------------------------------------
 */

export const getStatus = (state: State): string => getState(state).status;
export const getOpenedStatus = (state: State): string =>
  getState(state).openedStatus;

export const getLiveChatLoadParams: (
  state: Object,
  dispatch: Function,
) => WidgetLoadParams = createSelector(
  [
    getEventSafely,
    getLiveChatEnvOptions,
    getMembership,
    getSelf,
    getOpenedStatus,
    getLocale,
    (_state, dispatch) => dispatch,
  ],
  (
    event,
    liveChatEnvOptions,
    membership,
    selfUser,
    openedStatus,
    locale,
    dispatch,
  ): WidgetLoadParams => ({
    onStatusChange: (newStatus) => {
      dispatch(updateStatus(newStatus));
    },
    sdkOptions: getLiveChatOptions(event),
    onOpenedStatusChange: (newStatus) => {
      dispatch(updateOpenedStatus(newStatus));
    },
    envSdkOptions: liveChatEnvOptions,
    name: selfUser ? getUserFullName(selfUser) : null,
    email: membership ? getMembershipEmailAddress(membership) : null,
    userId: selfUser ? getUserId(selfUser) : null,
    avatar: selfUser ? getUserAvatar(selfUser) : null,
    locale,
    accentColor: getEventColor(event),
  }),
);

const mapSdkActions = {
  [LIVE_CHAT_PROVIDER_INTERCOM]: intercomWidgetActions,
  [LIVE_CHAT_PROVIDER_TAWK]: tawkWidgetActions,
};

export const getLiveChatActions: (state: Object) => Object = createSelector(
  [getEventSafely],
  (event): InternalLiveChatActions => {
    const sdkOptions = getLiveChatOptions(event);
    const provider = sdkOptions && getLiveChatProvider(sdkOptions);

    const sdkActions = provider && mapSdkActions[provider];
    return (
      sdkActions || {
        load: () => {},
        kill: () => {},
        toggle: () => {},
        identify: () => {},
        update: () => {},
      }
    );
  },
);
